import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, MapPin, Camera, CheckCircle, AlertCircle, History, Save, X } from "lucide-react";
import { toast } from "sonner";

const OLD_METER_COMPANIES = ["L&T", "HPL", "Genus", "Secure", "Other"];

const TRANSFORMER_TYPES = [
  { value: "distribution", label: "DT" },
  { value: "private", label: "PVT" },
  { value: "agriculture_ptw", label: "PTW" },
  { value: "public_water_works", label: "PWW" },
  { value: "industrial", label: "IND" }
];

const PRIMARY_CAPACITIES = [16, 25, 63, 100];
const EXTENDED_CAPACITIES = [160, 250, 400, 630];

const CABLE_CONFIG = {
  16: { disabled: true, types: [], sizes: {} },
  25: { disabled: false, types: ["ABC"], sizes: { "ABC": [50] }, defaultType: "ABC", defaultSize: 50 },
  63: { disabled: false, types: ["ABC"], sizes: { "ABC": [70] }, defaultType: "ABC", defaultSize: 70 },
  100: { disabled: false, types: ["ABC", "1-Core"], sizes: { "ABC": [120], "1-Core": [100] }, defaultType: "ABC", defaultSize: 120 },
  160: { disabled: false, types: ["ABC", "1-Core"], sizes: { "ABC": [120], "1-Core": [100] }, defaultType: "ABC", defaultSize: 120 },
  250: { disabled: false, types: ["1-Core"], sizes: { "1-Core": [400] }, defaultType: "1-Core", defaultSize: 400 },
  400: { disabled: false, types: ["1-Core"], sizes: { "1-Core": [400, 630] }, defaultType: "1-Core", defaultSize: 400 },
  630: { disabled: false, types: ["1-Core"], sizes: { "1-Core": [630, 850] }, defaultType: "1-Core", defaultSize: 630 }
};

// Compact pill button
const Pill = ({ selected, onClick, children, color = "blue", size = "sm", testId }) => {
  const colors = {
    blue: selected ? "border-blue-500 bg-blue-500 text-white" : "border-slate-200 bg-white text-slate-600",
    orange: selected ? "border-orange-500 bg-orange-500 text-white" : "border-slate-200 bg-white text-slate-600",
    purple: selected ? "border-purple-500 bg-purple-500 text-white" : "border-slate-200 bg-white text-slate-600",
    teal: selected ? "border-teal-500 bg-teal-500 text-white" : "border-slate-200 bg-white text-slate-600",
    green: selected ? "border-green-500 bg-green-50 text-green-700" : "border-slate-200 bg-white text-slate-500",
    red: selected ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 bg-white text-slate-500",
  };
  const sizes = { xs: "px-2.5 py-1 text-xs", sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm" };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full font-bold border-2 transition-colors ${colors[color]} ${sizes[size]}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
};

export default function SurveyForm() {
  const { dtId } = useParams();
  const [searchParams] = useSearchParams();
  const feederCode = searchParams.get("feeder");
  const navigate = useNavigate();
  const { user } = useAuth();

  const [dt, setDT] = useState(null);
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);
  const [surveyHistory, setSurveyHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isNewDT] = useState(!dtId);
  const [showMoreCapacity, setShowMoreCapacity] = useState(false);
  const [customCapacity, setCustomCapacity] = useState("");
  const [gpsStatus, setGpsStatus] = useState("pending");

  const [form, setForm] = useState({
    dt_code: "", dt_name: "", dt_type: "distribution",
    feeder_code: feederCode || "", feeder_name: "", address: "",
    transformer_capacity_kva: "",
    old_meter_available: false, old_meter_company: "", old_meter_photo_url: null,
    cable_required: true, cable_type: "", cable_size_sqmm: "", cable_length_m: "",
    gps_lat: "", gps_lng: "", gps_accuracy_m: "",
    remarks: "", photos: []
  });

  useEffect(() => { captureGPS(); }, []);

  // Update cable defaults when capacity changes - auto-select type & size when only 1 option
  useEffect(() => {
    const capacity = parseInt(form.transformer_capacity_kva);
    if (capacity && CABLE_CONFIG[capacity]) {
      const config = CABLE_CONFIG[capacity];
      if (config.disabled) {
        setForm(f => ({ ...f, cable_required: false, cable_type: "", cable_size_sqmm: "", cable_length_m: "" }));
      } else {
        const newType = config.types?.length === 1 ? config.types[0] : (config.defaultType || "");
        const sizesForType = config.sizes?.[newType] || [];
        const newSize = sizesForType.length === 1 ? sizesForType[0].toString() : (config.defaultSize?.toString() || "");
        setForm(f => ({ ...f, cable_type: newType, cable_size_sqmm: newSize }));
      }
    }
  }, [form.transformer_capacity_kva]);

  useEffect(() => {
    if (dtId) {
      const fetchDT = async () => {
        try {
          const dtRes = await api.get(`/dt-master/${dtId}`);
          setDT(dtRes.data);
          setForm(f => ({
            ...f, dt_code: dtRes.data.dt_code || "", dt_name: dtRes.data.dt_name || "",
            dt_type: dtRes.data.dt_type || "distribution",
            feeder_code: dtRes.data.feeder_code || "",
            feeder_name: dtRes.data.feeder_name || dtRes.data.feeder_code || "",
            address: dtRes.data.address || "",
            transformer_capacity_kva: dtRes.data.transformer_capacity_kva?.toString() || ""
          }));
          try {
            const surveyRes = await api.get(`/installation/survey/${dtId}`);
            setExisting(surveyRes.data);
            setForm(f => ({
              ...f,
              transformer_capacity_kva: surveyRes.data.transformer_capacity_kva?.toString() || f.transformer_capacity_kva,
              old_meter_available: surveyRes.data.old_meter_available || false,
              old_meter_company: surveyRes.data.old_meter_company || "",
              old_meter_photo_url: surveyRes.data.old_meter_photo_url || null,
              cable_size_sqmm: surveyRes.data.cable_size_sqmm?.toString() || "",
              cable_length_m: surveyRes.data.cable_length_m?.toString() || "",
              remarks: surveyRes.data.remarks || "", photos: surveyRes.data.photos || []
            }));
          } catch {}
          try {
            const historyRes = await api.get(`/installation/survey/${dtId}/history`);
            setSurveyHistory(historyRes.data.surveys || []);
          } catch {}
        } catch {
          toast.error("DT not found");
          navigate(-1);
        }
      };
      fetchDT();
    } else if (feederCode) {
      const fetchFeederInfo = async () => {
        try {
          const res = await api.get(`/hierarchy?feeder_code=${feederCode}`);
          const feeder = res.data?.items?.find(f => f.feeder_code === feederCode);
          if (feeder) {
            let displayName = feeder.feeder_name || feederCode;
            if (displayName.startsWith("Feeder ") || !displayName) displayName = feeder.sub_station_name || displayName;
            setForm(f => ({ ...f, feeder_name: displayName, feeder_code: feederCode }));
          }
        } catch {}
      };
      fetchFeederInfo();
    }
  }, [dtId, feederCode, navigate]);

  const captureGPS = useCallback(() => {
    if (!navigator.geolocation) { setGpsStatus("error"); toast.error("GPS not supported on this device. Please use a device with location services"); return; }
    setGpsStatus("capturing");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({
          ...f, gps_lat: pos.coords.latitude.toFixed(6),
          gps_lng: pos.coords.longitude.toFixed(6),
          gps_accuracy_m: pos.coords.accuracy?.toFixed(0) || ""
        }));
        setGpsStatus("captured");
      },
      (err) => {
        setGpsStatus("error");
        if (err.code === 1) toast.error("Location permission denied. Please enable GPS in your browser/device settings");
        else if (err.code === 2) toast.error("GPS signal unavailable. Move to an open area and try again");
        else toast.error("GPS capture timed out. Please try again");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, []);

  const addWatermark = useCallback(async (imageDataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        
        // Resize to max 1280px on longest side (30x smaller payload)
        const MAX_DIM = 1280;
        let w = img.width, h = img.height;
        if (w > MAX_DIM || h > MAX_DIM) {
          const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        canvas.width = w; canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);
        
        const now = new Date();
        const dateStr = now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        const timeStr = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
        const lines = [
          form.dt_code || "NEW-DT",
          user?.employee_id?.slice(0, 8) || user?.id?.slice(0, 8) || "EMP",
          `${dateStr} ${timeStr}`,
          form.gps_lat && form.gps_lng ? `${form.gps_lat}, ${form.gps_lng}` : "GPS Pending"
        ];
        const fontSize = Math.max(12, Math.min(16, w / 40));
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.textAlign = "right";
        const padding = 10;
        const lineHeight = fontSize + 4;
        const totalHeight = lines.length * lineHeight;
        const maxWidth = lines.reduce((max, line) => Math.max(max, ctx.measureText(line).width), 0);
        ctx.fillStyle = "rgba(0,0,0,0.5)";
        ctx.fillRect(canvas.width - maxWidth - padding * 2, canvas.height - totalHeight - padding * 2, maxWidth + padding * 2, totalHeight + padding * 2);
        ctx.fillStyle = "#FFF";
        lines.forEach((line, i) => {
          ctx.fillText(line, canvas.width - padding, canvas.height - padding - (lines.length - 1 - i) * lineHeight);
        });
        resolve(canvas.toDataURL("image/jpeg", 0.6));
      };
      img.src = imageDataUrl;
    });
  }, [form.dt_code, form.gps_lat, form.gps_lng, user]);

  const handlePhoto = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const watermarked = await addWatermark(ev.target.result);
      setForm(f => { const p = [...f.photos]; p[index] = watermarked; return { ...f, photos: p }; });
      toast.success("Photo captured");
    };
    reader.readAsDataURL(file);
  };

  const handleOldMeterPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const watermarked = await addWatermark(ev.target.result);
      setForm(f => ({ ...f, old_meter_photo_url: watermarked }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.dt_code) { toast.error("DT Code is required"); return; }
    if (!form.feeder_code) { toast.error("Feeder Code is required"); return; }
    if (!form.gps_lat || !form.gps_lng) { toast.error("GPS location not captured. Please enable location services and tap 'Capture GPS'"); return; }
    if (!form.transformer_capacity_kva) { toast.error("Transformer capacity (KVA) is required"); return; }
    if (form.old_meter_available && !form.old_meter_company) { toast.error("Old meter company is required when old meter is available"); return; }
    if (!form.photos.some(p => p)) { toast.error("At least 1 photo is required. Please capture a site photo"); return; }

    setLoading(true);
    try {
      let targetDtId = dtId;
      if (isNewDT || !dtId) {
        const dtRes = await api.post("/dt-master", {
          dt_code: form.dt_code, dt_name: form.dt_name || form.dt_code,
          dt_type: form.dt_type || "distribution", feeder_code: form.feeder_code,
          address: form.address, gps_lat: parseFloat(form.gps_lat), gps_lng: parseFloat(form.gps_lng),
          transformer_capacity_kva: parseFloat(form.transformer_capacity_kva)
        });
        targetDtId = dtRes.data.id;
      }
      await api.post("/installation/survey", {
        dt_id: targetDtId, transformer_capacity_kva: parseFloat(form.transformer_capacity_kva),
        old_meter_available: form.old_meter_available,
        old_meter_company: form.old_meter_available ? form.old_meter_company : null,
        old_meter_photo_url: form.old_meter_available ? form.old_meter_photo_url : null,
        cable_type: form.cable_required ? form.cable_type : null,
        cable_size_sqmm: form.cable_required && form.cable_size_sqmm ? parseFloat(form.cable_size_sqmm) : null,
        cable_length_m: form.cable_required && form.cable_length_m ? parseFloat(form.cable_length_m) : null,
        gps_lat: parseFloat(form.gps_lat), gps_lng: parseFloat(form.gps_lng),
        gps_accuracy_m: form.gps_accuracy_m ? parseFloat(form.gps_accuracy_m) : null,
        remarks: form.remarks, photos: form.photos.filter(p => p)
      });
      toast.success(`${existing ? "Resurvey (RCI)" : "Survey (CI)"} submitted!`);
      navigate(feederCode ? "/feeder-assignments" : `/dt-master/${targetDtId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setLoading(false); }
  };

  const isResurvey = existing !== null;
  const cableConfig = (() => {
    const cap = parseInt(form.transformer_capacity_kva);
    return CABLE_CONFIG[cap] || { disabled: false, types: ["ABC", "1-Core"], sizes: {} };
  })();
  const is16KVA = parseInt(form.transformer_capacity_kva) === 16;
  const photoLabels = ["Front", "Back", "Plate", "Cable"];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="survey-form-page">
      {/* Colored Header: CI / RCI */}
      <header className="sticky top-0 z-20 bg-gradient-to-r from-yellow-500 to-yellow-600 px-3 py-3" data-testid="survey-header">
        <div className="flex items-center gap-3">
          {/* Left: Back + Big Label */}
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => navigate(-1)} className="text-white/80 p-1" data-testid="survey-back-btn">
              <ArrowLeft size={20} />
            </button>
            <span className="text-2xl font-black text-white tracking-tight">
              {isResurvey ? "RCI" : "CI"}
            </span>
          </div>

          {/* Center: DT Code + Feeder */}
          <div className="flex-1 min-w-0 text-center">
            {dt && <div className="font-mono text-base font-bold text-white truncate">{dt.dt_code}</div>}
            {dt && <div className="text-[10px] text-white/70 truncate">{dt.feeder_name || dt.feeder_code}</div>}
            {!dt && form.feeder_name && <div className="text-[10px] text-white/70 truncate">{form.feeder_name}</div>}
          </div>

          {/* Right: GPS pill */}
          <button
            onClick={gpsStatus !== "captured" ? captureGPS : undefined}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold shrink-0 ${
              gpsStatus === "captured" ? "bg-green-400/90 text-green-900" :
              gpsStatus === "capturing" ? "bg-white/30 text-white animate-pulse" :
              gpsStatus === "error" ? "bg-red-400/90 text-red-900" : "bg-white/30 text-white"
            }`}
            data-testid="gps-status-pill"
          >
            <MapPin size={11} />
            {gpsStatus === "captured" ? "GPS" :
             gpsStatus === "capturing" ? "..." :
             gpsStatus === "error" ? "Retry" : "GPS"}
          </button>
          {surveyHistory.length > 0 && (
            <button onClick={() => setShowHistory(!showHistory)} className="p-1 text-white/70" data-testid="history-btn">
              <History size={16} />
            </button>
          )}
        </div>
      </header>

      {/* Survey History Modal */}
      {showHistory && surveyHistory.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end">
          <div className="bg-white w-full max-h-[60vh] rounded-t-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white px-3 py-2 border-b flex items-center justify-between">
              <span className="font-bold text-sm">History ({surveyHistory.length})</span>
              <button onClick={() => setShowHistory(false)}><X size={18} /></button>
            </div>
            <div className="p-3 space-y-2">
              {surveyHistory.map((s, i) => (
                <div key={i} className="p-2 bg-slate-50 rounded text-xs">
                  <div className="font-semibold">{s.status} - {s.performed_by_name}</div>
                  <div className="text-[10px] text-slate-400">{new Date(s.performed_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="flex-1 px-3 pt-3 pb-16 space-y-4">
        {/* DT Code */}
        <div className="flex items-center gap-3">
          <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-[72px]">DT Code *</label>
          <input
            type="text" className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-lg font-mono bg-white"
            placeholder="Enter DT Code" value={form.dt_code}
            onChange={e => setForm({ ...form, dt_code: e.target.value.toUpperCase() })}
            disabled={!!dtId} data-testid="dt-code-input"
          />
        </div>

        {/* DT Name */}
        <div className="flex items-center gap-3">
          <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-[72px]">DT Name</label>
          <input
            type="text" className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white"
            placeholder="Village, landmark..." value={form.dt_name}
            onChange={e => setForm({ ...form, dt_name: e.target.value })}
            data-testid="dt-name-input"
          />
        </div>

        {/* Transformer Category — inline: label | pills */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-20">Category *</label>
          <div className="flex gap-1.5">
            {TRANSFORMER_TYPES.map(t => (
              <Pill key={t.value} selected={form.dt_type === t.value} color="orange" size="sm"
                onClick={() => setForm({ ...form, dt_type: t.value })} testId={`dt-type-${t.value}`}>
                {t.label}
              </Pill>
            ))}
          </div>
        </div>

        {/* Rating (KVA) — inline: label | pills */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-20">Rating *</label>
          <div className="flex flex-wrap gap-1.5">
            {PRIMARY_CAPACITIES.map(cap => (
              <Pill key={cap} selected={form.transformer_capacity_kva === cap.toString()} color="blue" size="sm"
                onClick={() => setForm({ ...form, transformer_capacity_kva: cap.toString() })} testId={`capacity-${cap}`}>
                {cap}
              </Pill>
            ))}
            <Pill selected={showMoreCapacity || EXTENDED_CAPACITIES.includes(parseInt(form.transformer_capacity_kva))}
              color="blue" size="sm" onClick={() => setShowMoreCapacity(!showMoreCapacity)}>
              +
            </Pill>
          </div>
        </div>
        {showMoreCapacity && (
          <div className="flex flex-wrap gap-1.5 pl-[88px] mt-2">
            {EXTENDED_CAPACITIES.map(cap => (
              <Pill key={cap} selected={form.transformer_capacity_kva === cap.toString()} color="blue" size="sm"
                onClick={() => setForm({ ...form, transformer_capacity_kva: cap.toString() })} testId={`capacity-${cap}`}>
                {cap}
              </Pill>
            ))}
            <input type="number" placeholder="Other" value={customCapacity}
              onChange={e => { setCustomCapacity(e.target.value); if (e.target.value) setForm(f => ({ ...f, transformer_capacity_kva: e.target.value })); }}
              className="w-16 px-2 py-2 rounded-full text-sm font-bold border-2 border-slate-200 text-center"
            />
          </div>
        )}

        {/* Old Meter — inline: label | Y/N | company | camera */}
        <div className={is16KVA ? "opacity-40 pointer-events-none" : ""}>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-[106px] whitespace-nowrap">Old Meter?</label>
            <div className="flex gap-1.5">
              <Pill selected={form.old_meter_available} color="green" size="md"
                onClick={() => setForm({ ...form, old_meter_available: true })}>
                <span className="flex items-center gap-1"><CheckCircle size={12} />Y</span>
              </Pill>
              <Pill selected={!form.old_meter_available} color="red" size="md"
                onClick={() => setForm({ ...form, old_meter_available: false, old_meter_company: "", old_meter_photo_url: null })}>
                <span className="flex items-center gap-1"><AlertCircle size={12} />N</span>
              </Pill>
            </div>
            {form.old_meter_available && (
              <>
                <select className="flex-1 min-w-0 px-2 py-2 text-sm border border-slate-200 rounded-lg"
                  value={form.old_meter_company} onChange={e => setForm({ ...form, old_meter_company: e.target.value })}>
                  <option value="">Company</option>
                  {OLD_METER_COMPANIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <label className={`shrink-0 border-2 border-dashed rounded-lg p-2 cursor-pointer ${
                  form.old_meter_photo_url ? "border-green-300 text-green-600" : "border-slate-200 text-slate-400"
                }`}>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleOldMeterPhoto} />
                  {form.old_meter_photo_url ? <CheckCircle size={16} /> : <Camera size={16} />}
                </label>
              </>
            )}
          </div>
        </div>

        {/* Cable Change Required — inline: label | Y/N */}
        <div className={is16KVA ? "opacity-40 pointer-events-none" : ""}>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-[106px] whitespace-nowrap">Cable Change?</label>
            <div className="flex gap-1.5">
              <Pill selected={form.cable_required} color="green" size="md"
                onClick={() => setForm({ ...form, cable_required: true })}>
                <span className="flex items-center gap-1"><CheckCircle size={12} />Y</span>
              </Pill>
              <Pill selected={!form.cable_required} color="red" size="md"
                onClick={() => setForm({ ...form, cable_required: false, cable_type: "", cable_size_sqmm: "", cable_length_m: "" })}>
                <span className="flex items-center gap-1"><AlertCircle size={12} />N</span>
              </Pill>
            </div>
          </div>

          {/* Cable Type + Size + Length — grid layout aligned with Category pills */}
          {form.cable_required && (
            <div className="flex items-start gap-2 mt-3">
              <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-20 pt-4">Cable</label>
              <div className="flex-1 min-w-0">
                {/* Headers */}
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-0 mb-1">
                  <label className="text-xs font-bold text-slate-600 uppercase">Type</label>
                  <label className="text-xs font-bold text-slate-600 uppercase whitespace-nowrap">Size mm²</label>
                  <label className="text-xs font-bold text-slate-600 uppercase whitespace-nowrap">Len (mtr)</label>
                </div>
                {/* Values */}
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-0 items-start">
                  <div className="flex gap-1">
                    {(cableConfig.types?.length > 0 ? cableConfig.types : ["ABC", "1-Core"]).map(type => (
                      <Pill key={type} selected={form.cable_type === type} color="purple" size="sm"
                        onClick={() => {
                          const sizes = cableConfig.sizes?.[type] || [];
                          const autoSize = sizes.length === 1 ? sizes[0].toString() : "";
                          setForm({ ...form, cable_type: type, cable_size_sqmm: autoSize });
                        }}>
                        {type}
                      </Pill>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    {form.cable_type && (cableConfig.sizes?.[form.cable_type] || [50, 70, 100, 120, 400, 630, 850]).map(size => (
                      <Pill key={size} selected={form.cable_size_sqmm === size.toString()} color="teal" size="sm"
                        onClick={() => setForm({ ...form, cable_size_sqmm: size.toString() })}>
                        {size}
                      </Pill>
                    ))}
                  </div>
                  <div>
                    {form.cable_type && form.cable_size_sqmm && (
                      <input type="number"
                        className="w-14 px-2 py-1.5 text-xs font-bold border-2 border-slate-200 rounded-full text-center"
                        placeholder="mtr" value={form.cable_length_m}
                        onChange={e => setForm({ ...form, cable_length_m: e.target.value })}
                        data-testid="cable-length-input"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Photos — 4-col grid, larger boxes */}
        <div>
          <label className="text-xs font-bold text-slate-600 uppercase">Photos</label>
          <div className="grid grid-cols-4 gap-2 mt-1.5">
            {photoLabels.map((label, i) => (
              <label key={label} className={`relative border-2 border-dashed rounded-xl p-2 text-center cursor-pointer transition-colors ${
                form.photos[i] ? "border-green-300 bg-green-50" : "border-slate-200 hover:border-orange-300"
              }`} data-testid={`photo-${label.toLowerCase()}`}>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhoto(e, i)} />
                {form.photos[i] ? (
                  <div className="aspect-square flex items-center justify-center">
                    <img src={form.photos[i]} alt={label} className="max-h-full max-w-full object-cover rounded" />
                  </div>
                ) : (
                  <div className="aspect-square flex flex-col items-center justify-center">
                    <Camera size={22} className="text-slate-400" />
                    <span className="text-[10px] text-slate-500 font-bold mt-1">{label}</span>
                  </div>
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Remarks */}
        <div>
          <label className="text-xs font-bold text-slate-600 uppercase">Remarks</label>
          <textarea className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg resize-none mt-1.5 h-20"
            placeholder="Notes..." value={form.remarks}
            onChange={e => setForm({ ...form, remarks: e.target.value })}
            data-testid="remarks-input"
          />
        </div>
      </div>

      {/* Submit — fixed bottom */}
      <div className="sticky bottom-0 p-3 bg-white border-t border-slate-200" data-testid="submit-section">
        <button type="button" onClick={handleSubmit} disabled={loading}
          className="w-full h-11 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
          data-testid="submit-survey-btn">
          <Save size={16} />
          {loading ? "Submitting..." : isResurvey ? "Submit Resurvey" : "Submit Survey"}
        </button>
      </div>
    </div>
  );
}
